import { useState, useEffect } from 'react';
import { api } from '../services/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
];

const EMPTY_FIELD = {
  name: '',
  type: 'text',
  label: '',
  required: false,
  placeholder: '',
  helpText: '',
  options: [],
};

function FormManagement() {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // formDefId or 'new'
  const [saving, setSaving] = useState(false);
  const [perpetualLinks, setPerpetualLinks] = useState({}); // formDefId -> { formUrl, loading }
  const [formState, setFormState] = useState({
    title: '',
    description: '',
    fields: [],
    haloConfig: { ticketTypeId: 1, category: '', priority: 'Medium', autoClose: false },
  });

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    try {
      setLoading(true);
      const data = await api.listFormDefinitions();
      setDefinitions(data);
    } catch (error) {
      console.error('Error loading form definitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => {
    setEditing('new');
    setFormState({
      title: '',
      description: '',
      fields: [{ ...EMPTY_FIELD }],
      haloConfig: { ticketTypeId: 1, category: '', priority: 'Medium', autoClose: false },
    });
  };

  const startEdit = (def) => {
    setEditing(def.formDefId);
    setFormState({
      title: def.title,
      description: def.description || '',
      fields: def.fields || [],
      haloConfig: def.haloConfig || { ticketTypeId: 1, category: '', priority: 'Medium', autoClose: false },
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setFormState({ title: '', description: '', fields: [], haloConfig: {} });
  };

  const handleSave = async () => {
    if (!formState.title.trim()) {
      alert('Title is required');
      return;
    }
    if (formState.fields.length === 0) {
      alert('At least one field is required');
      return;
    }

    // Validate fields have names and labels
    for (let i = 0; i < formState.fields.length; i++) {
      const f = formState.fields[i];
      if (!f.name.trim() || !f.label.trim()) {
        alert(`Field ${i + 1}: Name and Label are required`);
        return;
      }
      if (f.type === 'select' && (!f.options || f.options.length === 0)) {
        alert(`Field "${f.label}": Dropdown fields need at least one option`);
        return;
      }
    }

    try {
      setSaving(true);
      if (editing === 'new') {
        await api.createFormDefinition(formState);
      } else {
        await api.updateFormDefinition(editing, formState);
      }
      await loadDefinitions();
      cancelEdit();
    } catch (error) {
      console.error('Error saving form definition:', error);
      alert('Failed to save form definition');
    } finally {
      setSaving(false);
    }
  };

  const handlePerpetualLink = async (def) => {
    // If we already have the URL cached, just copy it
    if (perpetualLinks[def.formDefId]?.formUrl) {
      await navigator.clipboard.writeText(perpetualLinks[def.formDefId].formUrl);
      alert('Permanent link copied to clipboard!');
      return;
    }

    try {
      setPerpetualLinks(prev => ({ ...prev, [def.formDefId]: { loading: true } }));
      const result = await api.createPerpetualLink(def.formDefId);
      setPerpetualLinks(prev => ({ ...prev, [def.formDefId]: { formUrl: result.formUrl, loading: false } }));
      await navigator.clipboard.writeText(result.formUrl);
      alert('Permanent link created and copied to clipboard!');
    } catch (error) {
      console.error('Error creating perpetual link:', error);
      setPerpetualLinks(prev => ({ ...prev, [def.formDefId]: { loading: false } }));
      alert('Failed to create permanent link');
    }
  };

  const toggleActive = async (def) => {
    try {
      await api.updateFormDefinition(def.formDefId, { isActive: !def.isActive });
      await loadDefinitions();
    } catch (error) {
      console.error('Error toggling form definition:', error);
      alert('Failed to update form definition');
    }
  };

  // ─── Field management ───

  const addField = () => {
    setFormState(prev => ({
      ...prev,
      fields: [...prev.fields, { ...EMPTY_FIELD }],
    }));
  };

  const removeField = (index) => {
    setFormState(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const updateField = (index, key, value) => {
    setFormState(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => {
        if (i !== index) return f;
        const updated = { ...f, [key]: value };
        // Auto-generate name from label if name is empty or was auto-generated
        if (key === 'label' && (!f.name || f.name === labelToName(f.label))) {
          updated.name = labelToName(value);
        }
        return updated;
      }),
    }));
  };

  const moveField = (index, direction) => {
    const newFields = [...formState.fields];
    const target = index + direction;
    if (target < 0 || target >= newFields.length) return;
    [newFields[index], newFields[target]] = [newFields[target], newFields[index]];
    setFormState(prev => ({ ...prev, fields: newFields }));
  };

  const labelToName = (label) => {
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // ─── Options management for select fields ───

  const addOption = (fieldIndex) => {
    const fields = [...formState.fields];
    fields[fieldIndex] = {
      ...fields[fieldIndex],
      options: [...(fields[fieldIndex].options || []), ''],
    };
    setFormState(prev => ({ ...prev, fields }));
  };

  const updateOption = (fieldIndex, optIndex, value) => {
    const fields = [...formState.fields];
    const options = [...fields[fieldIndex].options];
    options[optIndex] = value;
    fields[fieldIndex] = { ...fields[fieldIndex], options };
    setFormState(prev => ({ ...prev, fields }));
  };

  const removeOption = (fieldIndex, optIndex) => {
    const fields = [...formState.fields];
    fields[fieldIndex] = {
      ...fields[fieldIndex],
      options: fields[fieldIndex].options.filter((_, i) => i !== optIndex),
    };
    setFormState(prev => ({ ...prev, fields }));
  };

  // ─── Render ───

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-3 text-gray-600">Loading form definitions...</p>
      </div>
    );
  }

  // ─── Editor View ───
  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editing === 'new' ? 'Create Form Template' : 'Edit Form Template'}
          </h2>
          <button
            onClick={cancelEdit}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cancel
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Basic Info</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={formState.title}
                  onChange={(e) => setFormState(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Distribution List Update"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formState.description}
                  onChange={(e) => setFormState(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Instructions shown to the client at the top of the form"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Fields */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Fields ({formState.fields.length})
              </h3>
              <button
                onClick={addField}
                className="text-sm bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700"
              >
                + Add Field
              </button>
            </div>

            {formState.fields.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No fields yet. Click "+ Add Field" to get started.
              </p>
            )}

            <div className="space-y-4">
              {formState.fields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-500">Field {index + 1}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveField(index, -1)}
                        disabled={index === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        onClick={() => moveField(index, 1)}
                        disabled={index === formState.fields.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 p-1"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        onClick={() => removeField(index)}
                        className="text-red-400 hover:text-red-600 p-1 ml-2"
                        title="Remove field"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(index, 'label', e.target.value)}
                        placeholder="e.g., Email Address"
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Field Name *</label>
                      <input
                        type="text"
                        value={field.name}
                        onChange={(e) => updateField(index, 'name', e.target.value)}
                        placeholder="Auto-generated from label"
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                      <select
                        value={field.type}
                        onChange={(e) => updateField(index, 'type', e.target.value)}
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      >
                        {FIELD_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Placeholder</label>
                      <input
                        type="text"
                        value={field.placeholder || ''}
                        onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                        placeholder="Optional placeholder text"
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Help Text</label>
                      <input
                        type="text"
                        value={field.helpText || ''}
                        onChange={(e) => updateField(index, 'helpText', e.target.value)}
                        placeholder="Optional help text below the field"
                        className="w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center">
                    <input
                      type="checkbox"
                      id={`required-${index}`}
                      checked={field.required || false}
                      onChange={(e) => updateField(index, 'required', e.target.checked)}
                      className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                    />
                    <label htmlFor={`required-${index}`} className="ml-2 text-sm text-gray-700">
                      Required
                    </label>
                  </div>

                  {/* Options for select fields */}
                  {field.type === 'select' && (
                    <div className="mt-3 pl-3 border-l-2 border-primary-200">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-600">Dropdown Options</label>
                        <button
                          onClick={() => addOption(index)}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          + Add Option
                        </button>
                      </div>
                      {(field.options || []).map((opt, optIdx) => (
                        <div key={optIdx} className="flex items-center gap-2 mb-1">
                          <input
                            type="text"
                            value={opt}
                            onChange={(e) => updateOption(index, optIdx, e.target.value)}
                            placeholder={`Option ${optIdx + 1}`}
                            className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                          />
                          <button
                            onClick={() => removeOption(index, optIdx)}
                            className="text-red-400 hover:text-red-600 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      {(!field.options || field.options.length === 0) && (
                        <p className="text-xs text-gray-400">No options yet</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Halo Config */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Halo PSA Ticket Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Type ID</label>
                <input
                  type="number"
                  value={formState.haloConfig.ticketTypeId || ''}
                  onChange={(e) => setFormState(prev => ({
                    ...prev,
                    haloConfig: { ...prev.haloConfig, ticketTypeId: parseInt(e.target.value) || null },
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={formState.haloConfig.category || ''}
                  onChange={(e) => setFormState(prev => ({
                    ...prev,
                    haloConfig: { ...prev.haloConfig, category: e.target.value },
                  }))}
                  placeholder="e.g., Distribution List"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formState.haloConfig.priority || 'Medium'}
                  onChange={(e) => setFormState(prev => ({
                    ...prev,
                    haloConfig: { ...prev.haloConfig, priority: e.target.value },
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="autoClose"
                  checked={formState.haloConfig.autoClose || false}
                  onChange={(e) => setFormState(prev => ({
                    ...prev,
                    haloConfig: { ...prev.haloConfig, autoClose: e.target.checked },
                  }))}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <label htmlFor="autoClose" className="ml-2 text-sm text-gray-700">
                  Auto-close ticket after submission
                </label>
              </div>
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end gap-3">
            <button
              onClick={cancelEdit}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editing === 'new' ? 'Create Form Template' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Form Templates</h2>
        <button
          onClick={startNew}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 text-sm font-medium"
        >
          + New Form Template
        </button>
      </div>

      {definitions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500 mb-4">No form templates yet</p>
          <button
            onClick={startNew}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 text-sm"
          >
            Create your first form template
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {definitions.map((def) => (
            <div key={def.formDefId} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-medium text-gray-900">{def.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      def.isActive !== false
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {def.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {def.description && (
                    <p className="text-sm text-gray-600 mb-2">{def.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{def.fields?.length || 0} fields</span>
                    {def.haloConfig?.category && <span>Category: {def.haloConfig.category}</span>}
                    {def.haloConfig?.priority && <span>Priority: {def.haloConfig.priority}</span>}
                    {def.haloConfig?.autoClose && <span>Auto-close</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handlePerpetualLink(def)}
                    disabled={perpetualLinks[def.formDefId]?.loading}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium px-3 py-1 rounded hover:bg-indigo-50 disabled:opacity-50"
                    title={perpetualLinks[def.formDefId]?.formUrl || def.perpetualFormToken ? 'Copy permanent link' : 'Generate permanent link'}
                  >
                    {perpetualLinks[def.formDefId]?.loading
                      ? 'Creating...'
                      : perpetualLinks[def.formDefId]?.formUrl || def.perpetualFormToken
                        ? 'Copy Link'
                        : 'Permanent Link'}
                  </button>
                  <button
                    onClick={() => startEdit(def)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium px-3 py-1 rounded hover:bg-primary-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => toggleActive(def)}
                    className={`text-sm font-medium px-3 py-1 rounded ${
                      def.isActive !== false
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                    }`}
                  >
                    {def.isActive !== false ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>

              {/* Permanent link display */}
              {(perpetualLinks[def.formDefId]?.formUrl || def.perpetualFormToken) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-indigo-600">Permanent link:</span>
                    <code className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded truncate max-w-md">
                      {perpetualLinks[def.formDefId]?.formUrl || `${window.location.origin}/f/${def.perpetualFormToken}`}
                    </code>
                  </div>
                </div>
              )}

              {/* Field preview */}
              {def.fields && def.fields.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex flex-wrap gap-2">
                    {def.fields.map((field, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                        {field.label}
                        <span className="ml-1 text-gray-400">({field.type})</span>
                        {field.required && <span className="ml-1 text-red-400">*</span>}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FormManagement;
